import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const financialLedgerSlice = createSlice({
    name: "financialLedger",
    initialState: {
        page: 0,
        pageCount: 0,
        totalRow: 0,
        listData: null,
        stats: null,
        detailData: null,
        exporting: false,
    },
    reducers: {
        doGetLedgerListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload.current_page;
            state.pageCount = action.payload.page_count;
            state.totalRow = action.payload.total_row;
        },
        doGetLedgerListFail: (state) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.totalRow = 0;
        },
        doGetLedgerStatsSuccess: (state, action) => {
            state.stats = action.payload;
        },
        doGetLedgerStatsFail: (state) => {
            state.stats = null;
        },
        doGetLedgerDetailSuccess: (state, action) => {
            state.detailData = action.payload.transaction;
        },
        doGetLedgerDetailFail: (state) => {
            state.detailData = null;
        },
        setExporting: (state, action) => {
            state.exporting = action.payload;
        },
        resetFinancialLedgerState: (state) => {
            state.listData = null;
            state.stats = null;
            state.page = 0;
            state.pageCount = 0;
            state.totalRow = 0;
        },
        resetFinancialLedgerDetailState: (state) => {
            state.detailData = null;
        },
    },
});

// Build a query string from the shared filter set.
const buildQuery = (param) => {
    const parts = [];
    if (param.page) parts.push(`page=${param.page}`);
    if (param.limit) parts.push(`size=${param.limit}`);
    if (param.keyword) parts.push(`keyword=${encodeURIComponent(param.keyword)}`);
    if (param.type && param.type !== "ALL") parts.push(`type=${param.type}`);
    if (param.status && param.status !== "ALL") parts.push(`status=${param.status}`);
    if (param.user_id) parts.push(`user_id=${param.user_id}`);
    if (param.date_from) parts.push(`date_from=${param.date_from}`);
    if (param.date_to) parts.push(`date_to=${param.date_to}`);
    if (param.sort_by) parts.push(`sort_by=${param.sort_by}`);
    return parts.join("&");
};

export const doGetLedgerList = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/financial-ledger?${buildQuery(param)}`;
        const response = await axios.get(url);
        dispatch(doGetLedgerListSuccess(response.data));
    } catch (error) {
        dispatch(doGetLedgerListFail(error.response));
    }
};

// Summary cards are platform-wide, so they take no filter params.
export const doGetLedgerStats = () => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/financial-ledger/stats`);
        dispatch(doGetLedgerStatsSuccess(response.data));
    } catch (error) {
        dispatch(doGetLedgerStatsFail(error.response));
    }
};

export const doGetLedgerDetail = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/financial-ledger/${param.transaction_id}`;
        const response = await axios.get(url);
        dispatch(doGetLedgerDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetLedgerDetailFail(error.response));
    }
};

// Download the filtered ledger as a CSV file (streamed as a blob).
export const doExportLedgerCsv = (param) => async (dispatch) => {
    try {
        dispatch(setExporting(true));
        const url = `/api/admin/financial-ledger/export?${buildQuery({ ...param, page: null, limit: null })}`;
        const response = await axios.get(url, { responseType: "blob" });

        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", `transactions-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        // no-op: keep the UI responsive even if the export fails
    } finally {
        dispatch(setExporting(false));
    }
};

export const {
    doGetLedgerListSuccess, doGetLedgerListFail,
    doGetLedgerStatsSuccess, doGetLedgerStatsFail,
    doGetLedgerDetailSuccess, doGetLedgerDetailFail,
    setExporting,
    resetFinancialLedgerState, resetFinancialLedgerDetailState,
} = financialLedgerSlice.actions;

export const getLedgerList = (state) => state.financialLedger?.listData;
export const getLedgerListPage = (state) => state.financialLedger?.page;
export const getLedgerListPageCount = (state) => state.financialLedger?.pageCount;
export const getLedgerListTotalRow = (state) => state.financialLedger?.totalRow;
export const getLedgerStats = (state) => state.financialLedger?.stats;
export const getLedgerDetail = (state) => state.financialLedger?.detailData;
export const getLedgerExporting = (state) => state.financialLedger?.exporting;

export default financialLedgerSlice.reducer;
